import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["fileInput"]
  static values = { uploadUrl: String, messagesUrl: String }

  connect() {
    // Clipboard paste & drag-drop on the chat textarea
    const textarea = this.element.querySelector("[data-chat-room-target='input']")
    if (textarea) {
      this.boundPaste = (e) => this.handlePaste(e)
      this.boundDrop = (e) => this.handleDrop(e)
      this.boundDragOver = (e) => this.handleDragOver(e)
      textarea.addEventListener("paste", this.boundPaste)
      textarea.addEventListener("drop", this.boundDrop)
      textarea.addEventListener("dragover", this.boundDragOver)
    }
  }

  disconnect() {
    const textarea = this.element.querySelector("[data-chat-room-target='input']")
    if (textarea) {
      textarea.removeEventListener("paste", this.boundPaste)
      textarea.removeEventListener("drop", this.boundDrop)
      textarea.removeEventListener("dragover", this.boundDragOver)
    }
  }

  // + 버튼 클릭
  openPicker() {
    this.fileInputTarget.click()
  }

  handleFileSelect(event) {
    Array.from(event.target.files).forEach(file => this.upload(file))
    event.target.value = ""
  }

  handlePaste(event) {
    const items = event.clipboardData?.items
    if (!items) return
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        event.preventDefault()
        this.upload(item.getAsFile())
        return
      }
    }
  }

  handleDrop(event) {
    const files = event.dataTransfer?.files
    if (!files?.length) return
    event.preventDefault()
    Array.from(files).forEach(file => this.upload(file))
  }

  handleDragOver(event) {
    if (event.dataTransfer?.types?.includes("Files")) event.preventDefault()
  }

  async upload(file) {
    if (this.element.dataset.marketplaceClosed === "true") return
    if (file.size > 10 * 1024 * 1024) {
      this.showToast("파일 크기는 10MB 이하여야 합니다.", "error")
      return
    }

    const toastId = this.showToast(`${file.name} 업로드 중...`, "loading")
    const formData = new FormData()
    formData.append("file", file)

    try {
      const res = await fetch(this.uploadUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": this.csrfToken() },
        body: formData
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || "업로드 실패")

      this.removeToast(toastId)
      await this.sendFileMessage(data)
    } catch (err) {
      this.removeToast(toastId)
      this.showToast(err.message, "error")
      console.error("Upload error:", err)
    }
  }

  async sendFileMessage(fileData) {
    const messageType = fileData.is_image ? "image" : "file"
    const content = fileData.is_image ? "[이미지]" : `[파일: ${fileData.filename}]`

    const body = new FormData()
    body.append("message[message_type]", messageType)
    body.append("message[content]", content)
    body.append("message[file_url]", fileData.url)
    body.append("message[file_name]", fileData.filename)
    body.append("message[file_type]", fileData.file_type)
    body.append("message[file_size]", fileData.file_size)

    const res = await fetch(this.messagesUrlValue, {
      method: "POST",
      headers: {
        "X-CSRF-Token": this.csrfToken(),
        "Accept": "text/vnd.turbo-stream.html, text/html"
      },
      body
    })
    if (!res.ok) throw new Error("메시지 전송 실패")
  }

  // Toast: loading / error / success
  showToast(message, type = "info") {
    const id = `toast-${Date.now()}`
    const el = document.createElement("div")
    el.id = id
    el.className = [
      "fixed bottom-28 left-1/2 -translate-x-1/2 z-50",
      "flex items-center gap-2 px-4 py-2 rounded-full shadow-lg text-sm text-white",
      type === "error" ? "bg-red-600" : "bg-gray-800"
    ].join(" ")

    if (type === "loading") {
      el.innerHTML = `<div class="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full flex-shrink-0"></div><span>${this.escapeHtml(message)}</span>`
    } else {
      el.textContent = message
      setTimeout(() => el.remove(), 3000)
    }

    document.body.appendChild(el)
    return id
  }

  removeToast(id) {
    document.getElementById(id)?.remove()
  }

  csrfToken() {
    return document.querySelector("[name='csrf-token']")?.content
  }

  escapeHtml(text) {
    const d = document.createElement("div")
    d.textContent = text
    return d.innerHTML
  }
}
